import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchTiers, deleteTier } from '@/store/thunks/tierThunks';
import type { Tier } from '@/types';
import { checkPermission } from '@/utils/checkPermission';
import { filterListByQuery } from '@/utils/filterList';
import Page from '@/components/Page';
import { AddButton, FilterInput, Modal, DataTable, ConfirmModal, ListWithPagination, TableActions } from '@/components/ui';
import TierForm from '@/components/tiers/TierForm';

const PAGE_SIZE = 10;

export default function TierPage() {
  const dispatch = useAppDispatch();
  const tiers = useAppSelector((s: RootState) => s.tier.tiers);
  const { isAdmin, authorities } = useAppSelector((s: RootState) => s.auth);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [toDelete, setToDelete] = useState<Tier | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterQuery, setFilterQuery] = useState('');

  useEffect(() => {
    dispatch(fetchTiers());
  }, [dispatch]);

  const filteredTiers = filterListByQuery(tiers, filterQuery, ['code', 'nom', 'type']);
  const totalPages = Math.max(1, Math.ceil(filteredTiers.length / PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages - 1);
  const slicedTiers = filteredTiers.slice(effectivePage * PAGE_SIZE, (effectivePage + 1) * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages - 1));
  }, [totalPages]);

  const canCreate = isAdmin || checkPermission('CREATE_TIER', authorities);
  const canUpdate = isAdmin || checkPermission('UPDATE_TIER', authorities);
  const canDelete = isAdmin || checkPermission('DELETE_TIER', authorities);

  const columns = [
    { key: 'id', label: 'Id' },
    { key: 'code', label: 'Code' },
    { key: 'nom', label: 'Nom' },
    { key: 'type', label: 'Type' },
  ];

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = () => {
    if (toDelete) {
      dispatch(deleteTier(toDelete));
      setToDelete(null);
      setConfirmOpen(false);
    }
  };

  return (
    <Page title="Tiers">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">Liste des tiers</h2>
            <FilterInput value={filterQuery} onChange={setFilterQuery} placeholder="Rechercher (code, nom, type)…" />
          </div>
          <AddButton label="Créer un tier" disabled={!canCreate} onClick={() => { setEditing(null); setModalOpen(true); }} />
        </div>

        <ListWithPagination
          page={effectivePage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        >
          <DataTable<Tier>
            columns={columns}
            data={slicedTiers}
            keyField="id"
            emptyMessage="Aucun tier"
            actions={(row) => (
              <TableActions
                onEdit={() => { setEditing(row); setModalOpen(true); }}
                onDelete={() => { setToDelete(row); setConfirmOpen(true); }}
                editDisabled={!canUpdate}
                deleteDisabled={!canDelete}
              />
            )}
          />
        </ListWithPagination>
      </div>

      <Modal open={modalOpen} onClose={handleCloseModal} title={editing ? 'Modifier le tier' : 'Créer un tier'}>
        <TierForm
          tier={editing ?? undefined}
          type={editing ? 'update' : 'create'}
          onClose={handleCloseModal}
          onSuccess={handleCloseModal}
        />
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        message="Voulez-vous supprimer ce tier ?"
      />
    </Page>
  );
}
